import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { UserAdminSummary } from '@ventin/shared';
import { useCompanies, useReassignUser } from '@/lib/adminApi';

interface Props {
  user: UserAdminSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * ReassignUserDialog — move a CLIENT user to a different Company (USR-03).
 *
 * Invariant: this dialog is only meaningful for CLIENT users. The parent
 * UsersPage gates the menu item; we additionally bail out here if a caller
 * opens it with a non-CLIENT user (defense in depth).
 *
 * Design note: we surface a DISABLED "No company (remove assignment)" option
 * with a tooltip explaining why, per plan must_haves. This makes the ADMIN/
 * SUPPORT escape hatch visible but impossible to select — reinforcing the
 * role/company invariant in the UI instead of hiding it.
 */
export function ReassignUserDialog({ user, open, onOpenChange }: Props) {
  const [companyId, setCompanyId] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: companiesData } = useCompanies();
  const companies = useMemo(() => companiesData?.companies ?? [], [companiesData]);
  const reassignMut = useReassignUser();

  useEffect(() => {
    if (!open) return;
    setFormError(null);
    setCompanyId(user?.companyId ?? '');
  }, [open, user]);

  const isClient = user?.role === 'CLIENT';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    if (!isClient) {
      setFormError('Only CLIENT users can be reassigned');
      return;
    }
    if (!companyId) {
      setFormError('Pick a destination company');
      return;
    }
    setFormError(null);
    setBusy(true);
    try {
      await reassignMut.mutateAsync({ id: user.id, companyId });
      toast.success('User reassigned');
      onOpenChange(false);
    } catch {
      setFormError("Couldn't save — try again");
      toast.error("Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reassign User</DialogTitle>
          <DialogDescription>
            Move {user?.fullName ?? 'this user'} to a different Company. Historical tickets and
            sessions remain under the original Company.
          </DialogDescription>
        </DialogHeader>
        {!isClient ? (
          <Alert variant="destructive">
            <AlertDescription>Only CLIENT users can be reassigned.</AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reassign-company">Company</Label>
              <select
                id="reassign-company"
                name="companyId"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              >
                <option value="">Select a company…</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
                <option value="__none__" disabled title="CLIENT users must belong to a Company">
                  No company (remove assignment) — not allowed for CLIENT
                </option>
              </select>
              <p className="text-xs text-muted-foreground">
                CLIENT users must always belong to a Company.
              </p>
            </div>
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Reassign'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
